import { useState } from 'react';
import { useAuth } from '../graphql/hooks/useAuth';
import { useUser } from '../graphql/hooks/useUserDashboard';
import { PersonalInfoSection } from './PersonalInfoSection';
import { DEFAULT_ENDPOINT_URL_EXPORT } from '../graphql/client';
import { Download, ChevronLeft, ChevronRight, MoreVertical } from 'lucide-react';
import { useGetDatevDocumentsByUser } from '../graphql/hooks/useAdminDashboard';
import { MonthPicker } from './core/Monthpicker';
import { useDocumentsForUser } from '../graphql/hooks/useAdminDashboard';
import { UserDocumentPanel } from './UserDocumentPanel';

type MainTab = 'personal-info' | 'documents' | 'my-documents';
type FileTypeFilter = 'all' | 'zip' | 'pdf';

export const UserProfileView = () => {
  const now = new Date();

  const { user, userType, userId, token } = useAuth();
  const { data: userData } = useUser();
  const [activeMainTab, setActiveMainTab] = useState<MainTab>('personal-info');
  const [isEditing, setIsEditing] = useState(false);

  const [fileTypeFilter, setFileTypeFilter] = useState<FileTypeFilter>('all');
  const [filterMode, setFilterMode] = useState<'single' | 'range'>('single');
  const [singleMonth, setSingleMonth] = useState('');
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const { data: documentsSorted = [] } = useDocumentsForUser(userId ?? '');

  const { data: datevDocuments } = useGetDatevDocumentsByUser(
    100,
    userId ? Number(userId) : undefined
  );

  const filteredDocs = (datevDocuments?.data ?? []).filter((doc) => {
    if (fileTypeFilter !== 'all' && doc.file_type !== fileTypeFilter) return false;

    if (filterMode === 'single') {
      if (singleMonth && doc.month !== singleMonth) return false;
    } else {
      if (rangeFrom && doc.month < rangeFrom) return false;
      if (rangeTo && doc.month > rangeTo) return false;
    }

    return true;
  });

const mainTabs = [
    { id: 'personal-info' as MainTab, label: 'Persönliche Informationen' },
    { id: 'documents' as MainTab, label: 'Lohnbüro' },
    { id: 'my-documents' as MainTab, label: 'Dokumente' },
  ];

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) return parts[0][0] + parts[1][0];
    return name.substring(0, 2);
  };

  const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

  const formatMonthLabel = (value: string) => {
    const [y, m] = value.split('-');
    return `${monthNames[Number(m) - 1]} ${y}`;
  };

  return (
    <div className="bg-[#f5f5f5] min-h-screen">
      {/* Header Section */}
      <div className="bg-white border-b border-[#e9e9e9]">
        <div className="mx-auto px-12 py-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20
              bg-gradient-to-br from-[#6b3fa0] to-[#9b59b6]
              rounded-full flex items-center justify-center
              text-white text-[16px] sm:text-[20px] md:text-[24px]
              font-bold shadow-lg shrink-0">
              {getInitials(userData?.full_name || user?.name || '')}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className="px-3 py-1 bg-[#22c55e]/10 text-[#22c55e] text-[12px] font-medium rounded-full">Aktiv</span>
                {userType !== 'admin' && (
                  <span className="px-3 py-1 bg-[#0084c7]/10 text-[#0084c7] text-[12px] font-medium rounded-full">
                    {userData?.job_title || '-'}
                  </span>
                )}
                {(userType === 'admin' || 'superadmin' ) && (
                  <span className="px-3 py-1 bg-[#f59e0b]/10 text-[#f59e0b] text-[12px] font-medium rounded-full">Administrator</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto px-12">
          <div className="flex gap-6 border-b border-[#e9e9e9]">
            {mainTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveMainTab(tab.id)}
                className={`pb-4 text-[14px] font-medium transition-colors relative ${
                  activeMainTab === tab.id ? 'text-[#0084c7]' : 'text-[#6b6b6b] hover:text-[#1c1c1c]'
                }`}
              >
                {tab.label}
                {activeMainTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#0084c7]" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {activeMainTab === 'personal-info' && (
        <div className="max-w-[1400px] mx-auto px-6 py-6">
          <div className="flex gap-6">
            <div className="flex-1 w-[90%] mx-auto md:w-auto md:mx-0 bg-white rounded-[12px] border border-[#e9e9e9] p-6 md:p-8">
              <PersonalInfoSection userData={userData} isEditing={isEditing} />
            </div>
          </div>
        </div>
      )}

      {activeMainTab === 'documents' && (
        <div className="px-6 py-6">
          <div className="bg-white rounded-[12px] border border-[#e9e9e9] p-6 space-y-4">

            <div className="flex flex-wrap items-center gap-4">

              <div className="flex items-center gap-1 bg-[#f5f5f5] rounded-lg p-1">
                {(['all', 'pdf', 'zip'] as FileTypeFilter[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => setFileTypeFilter(type)}
                    className={`px-3 py-1.5 text-[13px] rounded-md transition-colors ${
                      fileTypeFilter === type
                        ? 'bg-white text-[#0084c7] shadow-sm font-medium'
                        : 'text-[#6b6b6b] hover:text-[#1c1c1c]'
                    }`}
                  >
                    {type === 'all' ? 'Alle' : type.toUpperCase()}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-1 bg-[#f5f5f5] rounded-lg p-1">
                <button
                  onClick={() => setFilterMode('single')}
                  className={`px-3 py-1.5 text-[13px] rounded-md transition-colors ${
                    filterMode === 'single'
                      ? 'bg-white text-[#0084c7] shadow-sm font-medium'
                      : 'text-[#6b6b6b] hover:text-[#1c1c1c]'
                  }`}
                >
                  Monat
                </button>
                <button
                  onClick={() => setFilterMode('range')}
                  className={`px-3 py-1.5 text-[13px] rounded-md transition-colors ${
                    filterMode === 'range'
                      ? 'bg-white text-[#0084c7] shadow-sm font-medium'
                      : 'text-[#6b6b6b] hover:text-[#1c1c1c]'
                  }`}
                >
                  Zeitraum
                </button>
              </div>

              {filterMode === 'single' ? (
                <div className="flex items-center gap-2">
                  <span className="text-[13px] text-[#6b6b6b]">Monat:</span>
                  <MonthPicker value={singleMonth} onChange={setSingleMonth} placeholder="Alle Monate" />
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-[13px] text-[#6b6b6b]">Von:</span>
                  <MonthPicker value={rangeFrom} onChange={setRangeFrom} placeholder="Von" />
                  <span className="text-[13px] text-[#6b6b6b]">Bis:</span>
                  <MonthPicker value={rangeTo} onChange={setRangeTo} placeholder="Bis" />
                </div>
              )}
            </div>

            {filteredDocs.length > 0 ? (
              <div>
                <div className="grid grid-cols-[100px_1fr_120px_150px] gap-4 px-4 py-3 mb-2 bg-[#f5f5f5] rounded-lg text-[12px] font-medium text-[#6b6b6b]">
                  <div>ID</div>
                  <div>Pfad</div>
                  <div>Monat</div>
                  <div>Aktion</div>
                </div>

                {filteredDocs.map((doc) => (
                  <div
                    key={doc.id}
                    className="grid grid-cols-[100px_1fr_120px_150px] gap-4 px-4 py-3 text-[14px] items-center hover:bg-[#f5f5f5] transition-colors border-b border-[#e9e9e9]"
                  >
                    <div className="text-[#6b6b6b]">{doc.id}</div>
                    <div className="text-[#6b6b6b] truncate">{doc.path}</div>
                    <div className="text-[#6b6b6b]">{doc.month}</div>
                    <div>
                      <button
                        onClick={async () => {
                          try {
                            const response = await fetch(
                              `${DEFAULT_ENDPOINT_URL_EXPORT}/datev/download/${doc.id}`,
                              {
                                method: 'GET',
                                headers: { Authorization: token },
                              }
                            );

                            if (!response.ok) {
                              console.error('Download failed:', await response.text());
                              return;
                            }

                            const blob = await response.blob();

                            if (doc.file_type === 'pdf') {
                              const pdfBlob = new Blob([blob], { type: 'application/pdf' });
                              const pdfUrl = window.URL.createObjectURL(pdfBlob);
                              window.open(pdfUrl, '_blank');
                            } else {
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = doc.path.split('/').pop() ?? `datev_${doc.id}.zip`;
                              a.click();
                              window.URL.revokeObjectURL(url);
                            }
                          } catch (err) {
                            console.error(err);
                          }
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] text-white bg-[#0084c7] hover:bg-[#006fa8] rounded-lg transition-colors"
                      >
                        <Download className="size-4" />
                        {doc.file_type === 'pdf' ? 'Vorschau' : 'Herunterladen'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-12 text-center">
                <p className="text-[#6b6b6b] text-[16px]">Keine Dokumente vorhanden</p>
              </div>
            )}
          </div>
        </div>
      )}
      {activeMainTab === 'my-documents' && (
        <div className="px-6 py-6"> 
          <div className="bg-white rounded-[12px] border border-[#e9e9e9] p-6 space-y-4">
            {documentsSorted.length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-[#6b6b6b] text-[16px]">Keine Dokumente vorhanden</p>
              </div>
            ) : (
              <div className="space-y-4">
                {documentsSorted.map(({ documentCategory, documents }) => (
                  <UserDocumentPanel
                    key={documentCategory.id}
                    categoryName={documentCategory.name}
                    documents={documents}
                    token={token}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}